import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { orderApi } from '@formosa/shared/api/order';
import { ApiError } from '@formosa/shared/api/request';
import { DEFAULT_FOLLOW_GLOBAL_SETTINGS, pickFollowGlobalSettingsFromTenantList } from '@formosa/shared/utils/globalFollowSettings';
import { formatThumbChange, normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';
import { useAuth } from '../../auth/AuthContext';
import { useWallet } from '../../context/WalletContext';
import useProductSearch from '../../context/ProductSearchContext';
import FuturesCandlestickChart from '../../components/trade/FuturesCandlestickChart';
import FuturesPositionCard from '../../components/trade/FuturesPositionCard';
import ContinueOrderModal from '../../components/trade/ContinueOrderModal';
import FuturesOrderModal from '../../components/trade/FuturesOrderModal';
import OrderSuccessModal from '../../components/trade/OrderSuccessModal';
import useFuturesChartSocket from '../../hooks/useFuturesChartSocket';
import useFuturesOddsSocket from '../../hooks/useFuturesOddsSocket';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import TradeConfirmModal from '../../components/trade/TradeConfirmModal';
import TradeSuccessBanner from '../../components/trade/TradeSuccessBanner';
import useRequireAuth from '../../hooks/useRequireAuth';
import { useTranslation } from '../../i18n/LocaleContext';
import {
  findFuturesProductByPeriod,
  formatOddsPercent,
  formatPrice,
  listPeriodTabsForSymbol,
  parseFuturesPlayConfig,
  pickFuturesProductBySymbol,
} from '../../utils/futuresProduct';
import { filterFollowOrdersBySymbol } from '../../utils/followOrder';

function formatCountdown(seconds) {
  if (seconds == null || seconds < 0) {
    return '00:00:00';
  }
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export default function FuturesDeliveryTradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get('symbol');
  const { isAuthenticated } = useAuth();
  const { refreshWallet } = useWallet();
  const requireAuth = useRequireAuth();
  const { openSearch } = useProductSearch();
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [primaryProduct, setPrimaryProduct] = useState(null);
  const [selectedPeriodSeconds, setSelectedPeriodSeconds] = useState(60);
  const [activeProduct, setActiveProduct] = useState(null);
  const [currentRound, setCurrentRound] = useState(null);
  const [sealCountdown, setSealCountdown] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [orderDirection, setOrderDirection] = useState('UP');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedRoundCode, setSelectedRoundCode] = useState('');
  const [orderAmount, setOrderAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [continueOpen, setContinueOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelSuccessOpen, setCancelSuccessOpen] = useState(false);
  const [followSettings, setFollowSettings] = useState(DEFAULT_FOLLOW_GLOBAL_SETTINGS);
  const [hasAvailableFollowOrders, setHasAvailableFollowOrders] = useState(false);

  const periodTabs = useMemo(
    () => listPeriodTabsForSymbol(products, primaryProduct?.marketSymbolCode),
    [products, primaryProduct?.marketSymbolCode]
  );

  const symbolCode = activeProduct?.marketSymbolCode || primaryProduct?.marketSymbolCode || '';
  const thumbMap = useMarketThumbSocket(symbolCode ? [symbolCode] : []);
  const thumb = symbolCode ? thumbMap[normalizeSymbolCode(symbolCode)] : null;
  const { bars, loading: chartLoading } = useFuturesChartSocket(activeProduct?.id, symbolCode);
  const liveOdds = useFuturesOddsSocket(currentRound?.roundCode);

  const displayLongOdds = liveOdds.goLongOdds ?? currentRound?.endGoLongOdds;
  const displayShortOdds = liveOdds.goShortOdds ?? currentRound?.endGoShortOdds;

  const visiblePositions = useMemo(() => {
    if (!symbolCode) {
      return positions;
    }
    return positions.filter((item) => item.marketSymbolCode === symbolCode);
  }, [positions, symbolCode]);

  function resolvePositionProduct(order) {
    if (!order?.productId) {
      return null;
    }
    return products.find((item) => item.id === order.productId) || null;
  }

  function handleCancelPosition(order) {
    if (!order?.id || cancellingOrderId) {
      return;
    }
    setCancelTarget(order);
  }

  async function handleConfirmCancel() {
    if (!cancelTarget?.id || cancellingOrderId) {
      return;
    }
    setCancellingOrderId(cancelTarget.id);
    setError('');
    try {
      await orderApi.cancelFuturesOrder({ id: cancelTarget.id });
      setCancelTarget(null);
      setCancelSuccessOpen(true);
      refreshWallet();
      await loadPositions();
      if (activeProduct?.id) {
        loadRound(activeProduct.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('trade.cancelFailed'));
    } finally {
      setCancellingOrderId(null);
    }
  }

  const priceChange = useMemo(() => formatThumbChange(thumb), [thumb]);

  const showFollowButton = useMemo(
    () =>
      followSettings.futureFollowOpen &&
      followSettings.showFollowOrderButton &&
      (!isAuthenticated || hasAvailableFollowOrders),
    [
      followSettings.futureFollowOpen,
      followSettings.showFollowOrderButton,
      isAuthenticated,
      hasAvailableFollowOrders,
    ]
  );

  const loadFollowSettings = useCallback(async () => {
    try {
      const list = await memberApi.getTenantConfig();
      setFollowSettings(pickFollowGlobalSettingsFromTenantList(list));
    } catch {
      setFollowSettings(DEFAULT_FOLLOW_GLOBAL_SETTINGS);
    }
  }, []);

  const loadAvailableFollowOrders = useCallback(async () => {
    if (
      !isAuthenticated ||
      !followSettings.futureFollowOpen ||
      !followSettings.showFollowOrderButton
    ) {
      setHasAvailableFollowOrders(false);
      return;
    }

    const marketSymbol = activeProduct?.marketSymbolCode || symbolCode || '';
    try {
      const page = await orderApi.listFollowOrders({ pageNo: 1, pageSize: 50 });
      const available = filterFollowOrdersBySymbol(page?.records || [], marketSymbol);
      setHasAvailableFollowOrders(available.length > 0);
    } catch {
      setHasAvailableFollowOrders(false);
    }
  }, [
    activeProduct?.marketSymbolCode,
    followSettings.futureFollowOpen,
    followSettings.showFollowOrderButton,
    isAuthenticated,
    symbolCode,
  ]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await orderApi.listFuturesProducts();
      const rows = Array.isArray(list) ? list : [];
      setProducts(rows);
      const primary = pickFuturesProductBySymbol(rows, symbolParam);
      setPrimaryProduct(primary);
      if (primary) {
        const cfg = parseFuturesPlayConfig(primary.playConfig);
        const tabs = listPeriodTabsForSymbol(rows, primary.marketSymbolCode);
        const defaultSeconds = tabs[0]?.seconds || cfg.periodSeconds || 60;
        setSelectedPeriodSeconds(defaultSeconds);
        setActiveProduct(
          findFuturesProductByPeriod(rows, primary.marketSymbolCode, defaultSeconds) || primary
        );
      }
    } catch (err) {
      setProducts([]);
      setPrimaryProduct(null);
      setError(err instanceof ApiError ? err.message : t('trade.loadProductsFailed'));
    } finally {
      setLoading(false);
    }
  }, [symbolParam]);

  const loadRound = useCallback(async (productId) => {
    if (!productId) {
      return;
    }
    try {
      const round = await orderApi.getCurrentFuturesRound(productId);
      setCurrentRound(round);
      setSealCountdown(
        round?.sealRemainingSeconds != null
          ? Math.max(0, Number(round.sealRemainingSeconds))
          : null
      );
    } catch {
      setCurrentRound(null);
      setSealCountdown(null);
    }
  }, []);

  const loadPositions = useCallback(async () => {
    if (!isAuthenticated) {
      setPositions([]);
      return;
    }
    try {
      const page = await orderApi.listFuturesOrders({
        pageNo: 1,
        pageSize: 20,
        state: 'BOOKED',
      });
      setPositions(page?.records || []);
    } catch {
      setPositions([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadFollowSettings();
  }, [loadFollowSettings]);

  useEffect(() => {
    loadAvailableFollowOrders();
  }, [loadAvailableFollowOrders]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !followSettings.futureFollowOpen ||
      !followSettings.showFollowOrderButton
    ) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadAvailableFollowOrders();
    }, 10000);
    return () => clearInterval(timer);
  }, [
    followSettings.futureFollowOpen,
    followSettings.showFollowOrderButton,
    isAuthenticated,
    loadAvailableFollowOrders,
  ]);

  useEffect(() => {
    if (!primaryProduct?.marketSymbolCode) {
      return;
    }
    const product =
      findFuturesProductByPeriod(
        products,
        primaryProduct.marketSymbolCode,
        selectedPeriodSeconds
      ) || primaryProduct;
    setActiveProduct(product);
  }, [products, primaryProduct, selectedPeriodSeconds]);

  useEffect(() => {
    if (!activeProduct?.id) {
      return undefined;
    }
    loadRound(activeProduct.id);
    const timer = setInterval(() => {
      loadRound(activeProduct.id);
    }, 10000);
    return () => clearInterval(timer);
  }, [activeProduct?.id, loadRound]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  useEffect(() => {
    if (sealCountdown == null) {
      return undefined;
    }
    const ticker = setInterval(() => {
      setSealCountdown((prev) => (prev == null || prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(ticker);
  }, [currentRound?.roundCode]);

  function openOrderModal(direction) {
    if (!isAuthenticated) {
      const returnPath = symbolParam
        ? `/trade/futures?symbol=${encodeURIComponent(symbolParam)}`
        : '/trade/futures';
      requireAuth(returnPath);
      return;
    }
    setOrderDirection(direction);
    setOrderAmount('');
    setSelectedRoundCode(currentRound?.roundCode || '');
    setError('');
    setOrderModalOpen(true);
  }

  function handleFollowOrderClick() {
    void (async () => {
      let settings = followSettings;
      try {
        const list = await memberApi.getTenantConfig();
        settings = pickFollowGlobalSettingsFromTenantList(list);
        setFollowSettings(settings);
      } catch {
        // 保留已載入設定
      }
      const followPath = settings.followSignalsOpen
        ? '/trade/futures/follow/signals'
        : '/trade/futures/follow/list';
      const targetPath = symbolParam
        ? `${followPath}?symbol=${encodeURIComponent(symbolParam)}`
        : followPath;
      requireAuth(targetPath);
    })();
  }

  async function handleConfirmOrder(payload = {}) {
    const product =
      findFuturesProductByPeriod(
        products,
        primaryProduct?.marketSymbolCode,
        selectedPeriodSeconds
      ) || activeProduct;
    const roundCode = payload.roundCode || selectedRoundCode;
    const productId = payload.productId || product?.id;

    if (!productId || !roundCode || !orderAmount || Number(orderAmount) <= 0) {
      return;
    }
    if (!isAuthenticated) {
      requireAuth('/trade/futures');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await orderApi.placeFuturesOrder({
        productId,
        roundCode,
        orderTrend: orderDirection,
        orderAmount: Number(orderAmount),
      });
      setOrderModalOpen(false);
      setActiveOrder({
        ...result,
        marketSymbolCode: product.marketSymbolCode,
        expireTime: result?.roundEndTime,
      });
      setSuccessOpen(true);
      refreshWallet();
      loadPositions();
      loadRound(productId);

      window.setTimeout(() => {
        setSuccessOpen(false);
        setContinueOpen(true);
      }, 1000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('trade.placeOrderFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const pairLabel = activeProduct?.name || activeProduct?.displayName || `${symbolCode}/USDT`;
  const displayCode = activeProduct?.displayName || symbolCode || t('common.dash');

  return (
    <div className={`futures-trade-page ${showFollowButton ? 'has-follow-actions' : ''}`}>
      <TradeSuccessBanner
        open={cancelSuccessOpen}
        message={t('trade.cancelSuccess')}
        onClose={() => setCancelSuccessOpen(false)}
      />

      <header className="futures-trade-page__header">
        <button
          type="button"
          className="futures-trade-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{pairLabel}</h1>
        <button
          type="button"
          className="futures-trade-page__search"
          aria-label={t('common.search')}
          onClick={openSearch}
        >
          ⌕
        </button>
      </header>

      {error && <div className="futures-trade-page__error">{error}</div>}
      {loading && <p className="futures-trade-page__status">{t('common.loading')}</p>}

      {!loading && activeProduct && (
        <>
          <section className="futures-trade-page__quote">
            <div className="futures-trade-page__symbol-row">
              <span className="futures-trade-page__badge">{displayCode.slice(0, 1)}</span>
              <div>
                <div className="futures-trade-page__symbol">{displayCode}</div>
                <div className="futures-trade-page__unit">{pairLabel}</div>
              </div>
            </div>
            <div className="futures-trade-page__price">{formatPrice(thumb?.price, 4)}</div>
            <div className={`futures-trade-page__change ${priceChange.positive ? 'is-up' : 'is-down'}`}>
              {priceChange.text}
            </div>
          </section>

          <div className="futures-trade-page__period-tabs">
            {(periodTabs.length ? periodTabs : [{ seconds: selectedPeriodSeconds, label: `${selectedPeriodSeconds}s` }]).map(
              (tab) => (
                <button
                  key={tab.key || tab.seconds}
                  type="button"
                  className={`futures-trade-page__period-tab ${
                    selectedPeriodSeconds === tab.seconds ? 'is-active' : ''
                  }`}
                  onClick={() => setSelectedPeriodSeconds(tab.seconds)}
                >
                  {tab.label}
                </button>
              )
            )}
          </div>

          <div className="futures-trade-page__round-bar">
            <div>
              <span aria-hidden>📅</span>
              <span>{currentRound?.startTime || '—'}</span>
            </div>
            <div className="is-countdown">
              <span aria-hidden>⏱</span>
              <span>{formatCountdown(sealCountdown)}</span>
            </div>
            <div>
              <span aria-hidden>📄</span>
              <span>{currentRound?.roundCode || '—'}</span>
            </div>
          </div>

          <section className="futures-trade-page__chart" aria-label={t('trade.chartAria')}>
            <FuturesCandlestickChart
              bars={bars}
              currentPrice={thumb?.price ?? thumb?.close}
              loading={chartLoading}
              height={200}
            />
          </section>

          <section className="futures-trade-page__positions">
            <div className="futures-trade-page__positions-head">
              <h2>{t('trade.positions')}</h2>
              {isAuthenticated && <Link to="/orders">☰</Link>}
            </div>
            {visiblePositions.length === 0 ? (
              <div className="futures-trade-page__empty">
                <div aria-hidden>📄</div>
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              <ul className="futures-trade-page__position-list">
                {visiblePositions.slice(0, 5).map((item) => (
                  <FuturesPositionCard
                    key={item.id || item.orderSerialId}
                    order={item}
                    product={resolvePositionProduct(item)}
                    currentPrice={thumb?.price ?? thumb?.close}
                    cancelling={cancellingOrderId === item.id}
                    onCancel={handleCancelPosition}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <div className={`futures-trade-page__actions ${showFollowButton ? 'has-follow' : ''}`}>
        {showFollowButton && (
          <button
            type="button"
            className="futures-trade-page__follow"
            onClick={handleFollowOrderClick}
          >
            {t('follow.oneClick')}
          </button>
        )}
        <div className="futures-trade-page__actions-row">
          <button type="button" className="futures-trade-page__buy is-up" onClick={() => openOrderModal('UP')}>
            <span>{t('trade.buyUp')}</span>
            <small>{formatOddsPercent(displayLongOdds)}</small>
          </button>
          <button type="button" className="futures-trade-page__buy is-down" onClick={() => openOrderModal('DOWN')}>
            <span>{t('trade.buyDown')}</span>
            <small>{formatOddsPercent(displayShortOdds)}</small>
          </button>
        </div>
      </div>

      <TradeConfirmModal
        open={Boolean(cancelTarget)}
        message={t('trade.cancelConfirm')}
        confirming={Boolean(cancellingOrderId)}
        onClose={() => !cancellingOrderId && setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
      />

      <FuturesOrderModal
        open={orderModalOpen}
        direction={orderDirection}
        products={products}
        primaryProduct={primaryProduct}
        selectedPeriodSeconds={selectedPeriodSeconds}
        currentRound={currentRound}
        liveOdds={liveOdds}
        currentPrice={thumb?.price}
        amount={orderAmount}
        submitting={submitting}
        onClose={() => setOrderModalOpen(false)}
        onPeriodChange={setSelectedPeriodSeconds}
        onRoundChange={setSelectedRoundCode}
        onAmountChange={setOrderAmount}
        onConfirm={handleConfirmOrder}
      />

      <OrderSuccessModal open={successOpen} onClose={() => setSuccessOpen(false)} />

      <ContinueOrderModal
        open={continueOpen}
        order={activeOrder}
        currentPrice={thumb?.price}
        onClose={() => setContinueOpen(false)}
        onContinue={() => {
          setContinueOpen(false);
          openOrderModal(orderDirection);
        }}
      />
    </div>
  );
}
